import type { useLoader } from "@app/react";
import { useEffect, useState } from "react";
import { BannerMessages } from "./banner";
import { Button } from "./button";
import { FormGroup } from "./form-group";
import { Group } from "./group";
import { IconInfo } from "./icons";
import { Input } from "./input";
import { Select, type SelectOption } from "./select";
import { tokens } from "./tokens";
import { Tooltip } from "./tooltip";

const DAY_OF_WEEK_OPTIONS: SelectOption[] = [
  { label: "Sunday", value: "0" },
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
];

export interface MaintenanceWindowValues {
  dayOfWeek: number;
  startHour: number;
  durationHours: number;
}

export interface MaintenanceWindowSettingsProps {
  idPrefix: string;
  ownerLabel: string;
  resetKey: string;
  values: MaintenanceWindowValues;
  loader: ReturnType<typeof useLoader>;
  onSave: (values: MaintenanceWindowValues) => void;
}

export const MaintenanceWindowSettings = ({
  idPrefix,
  ownerLabel,
  resetKey,
  values,
  loader,
  onSave,
}: MaintenanceWindowSettingsProps) => {
  const [dayOfWeek, setDayOfWeek] = useState<string>(`${values.dayOfWeek}`);
  const [startHour, setStartHour] = useState<number>(values.startHour);
  const [durationHours, setDurationHours] = useState<number>(
    values.durationHours,
  );

  useEffect(() => {
    setDayOfWeek(`${values.dayOfWeek}`);
    setStartHour(values.startHour);
    setDurationHours(values.durationHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const hasChanges =
    dayOfWeek !== `${values.dayOfWeek}` ||
    startHour !== values.startHour ||
    durationHours !== values.durationHours;

  const onSelectDayOfWeek = (opt: SelectOption) => {
    setDayOfWeek(opt.value);
  };

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSave({ dayOfWeek: Number(dayOfWeek), startHour, durationHours });
  };

  const onCancel = () => {
    setDayOfWeek(`${values.dayOfWeek}`);
    setStartHour(values.startHour);
    setDurationHours(values.durationHours);
  };

  return (
    <form onSubmit={onSubmitForm} className="flex flex-col gap-4 pt-4">
      <div className="flex items-center gap-1">
        <h4 className={tokens.type.h4}>Maintenance Window</h4>
        <Tooltip
          text={`A weekly window, in UTC, during which this ${ownerLabel} may be restarted by Aptible for maintenance.`}
        >
          <IconInfo
            className="block opacity-50 hover:opacity-100"
            variant="sm"
          />
        </Tooltip>
      </div>

      <Group variant="horizontal" size="sm">
        <FormGroup
          label="Day of Week"
          labelProps={{ className: tokens.type["small semibold darker"] }}
          htmlFor={`${idPrefix}-maintenance-day-of-week`}
        >
          <Select
            id={`${idPrefix}-maintenance-day-of-week`}
            ariaLabel="Day of Week"
            value={dayOfWeek}
            onSelect={onSelectDayOfWeek}
            options={DAY_OF_WEEK_OPTIONS}
          />
        </FormGroup>

        <FormGroup
          label="Start Hour (UTC)"
          labelProps={{ className: tokens.type["small semibold darker"] }}
          htmlFor={`${idPrefix}-maintenance-start-hour`}
        >
          <Input
            id={`${idPrefix}-maintenance-start-hour`}
            name={`${idPrefix}-maintenance-start-hour`}
            type="number"
            min={0}
            max={23}
            value={startHour}
            onChange={(e) => {
              const value = Number.parseInt(e.currentTarget.value, 10);
              setStartHour(
                Number.isNaN(value) ? 0 : Math.min(Math.max(0, value), 23),
              );
            }}
          />
        </FormGroup>

        <FormGroup
          label="Duration (hours)"
          labelProps={{ className: tokens.type["small semibold darker"] }}
          htmlFor={`${idPrefix}-maintenance-duration`}
        >
          <Input
            id={`${idPrefix}-maintenance-duration`}
            name={`${idPrefix}-maintenance-duration`}
            type="number"
            min={1}
            max={24}
            value={durationHours}
            onChange={(e) => {
              const value = Number.parseInt(e.currentTarget.value, 10);
              setDurationHours(
                Number.isNaN(value) ? 1 : Math.min(Math.max(1, value), 24),
              );
            }}
          />
        </FormGroup>
      </Group>

      <BannerMessages {...loader} />

      <Group variant="horizontal" size="sm">
        <Button
          className="w-40 semibold"
          type="submit"
          disabled={!hasChanges}
          isLoading={loader.isLoading}
        >
          Save Changes
        </Button>

        <Button variant="white" onClick={onCancel}>
          Cancel
        </Button>
      </Group>
    </form>
  );
};
